import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, Github, Chrome, Sparkles } from 'lucide-react';
import { initializeNewUserDefaults } from '@/lib/accountCreation';
import { blink } from '@/lib/blink'; // Use shared SDK instance

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableProviders, setAvailableProviders] = useState<string[]>([]);
  const [isSignUp, setIsSignUp] = useState(false); // Toggle between sign in and sign up

  useEffect(() => {
    // Subscribe to authentication state changes
    const unsubscribe = blink.auth.onAuthStateChanged((state) => {
      console.log('Auth state changed:', state);
      if (state.user && !state.isLoading) {
        console.log('User authenticated, navigating to dashboard');
        navigate('/');
      }
    });
    
    // Get available authentication providers
    getProviders();
    
    return () => {
      unsubscribe();
    };
  }, [navigate]);

  const getProviders = async () => {
    try {
      const providers = await blink.auth.getAvailableProviders();
      setAvailableProviders(providers);
    } catch (err) {
      console.error('Failed to get providers:', err);
      setAvailableProviders(['email', 'google', 'github']);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isSignUp) {
        console.log('Attempting sign up...');
        const result = await blink.auth.signUp({ email, password });
        console.log('Sign up result:', result);
        
        // Initialize defaults for new users (non-blocking)
        if (result?.id) {
          console.log('Initializing user defaults...');
          initializeNewUserDefaults(result.id, result.email || email).catch(err => {
            console.warn('Failed to initialize defaults (non-critical):', err);
          });
        }
        
        // Send email verification
        try {
          await blink.auth.sendEmailVerification();
          setSuccess('Account created! Please check your email to verify your account.');
        } catch (verifyErr) {
          console.warn('Failed to send verification email:', verifyErr);
          setSuccess('Account created successfully! You can now sign in.');
        }
        setLoading(false);
      } else {
        console.log('Attempting email authentication...');
        const result = await blink.auth.signInWithEmail(email, password);
        console.log('Authentication result:', result);
        
        // Initialize defaults for existing users (non-blocking, in case they were missed)
        if (result?.id) {
          console.log('Initializing user defaults...');
          initializeNewUserDefaults(result.id, result.email || email).catch(err => {
            console.warn('Failed to initialize defaults (non-critical):', err);
          });
        }
        
        // Navigation will be handled by onAuthStateChanged
        console.log('Authentication successful');
        // Keep loading state until navigation happens
      }
    } catch (err: any) {
      console.error('Authentication error:', err);
      
      // Handle specific error cases with better error extraction
      const errorMessage = err?.message || err?.error?.message || String(err);
      const errorCode = err?.code || err?.error?.code;
      
      if (errorCode === 'EMAIL_NOT_VERIFIED' || errorMessage.includes('EMAIL_NOT_VERIFIED') || errorMessage.includes('not verified')) {
        setError('Please verify your email before signing in. Check your inbox for verification link.');
        setSuccess('');
      } else if (errorCode === 'INVALID_CREDENTIALS' || errorMessage.includes('INVALID_CREDENTIALS') || errorMessage.includes('Invalid credentials') || errorMessage.includes('invalid email or password')) {
        setError('Invalid email or password. Please try again.');
      } else if (errorCode === 'RATE_LIMITED' || errorMessage.includes('RATE_LIMITED') || errorMessage.includes('too many')) {
        setError('Too many login attempts. Please wait a moment and try again.');
      } else if (errorMessage.includes('EMAIL_ALREADY_EXISTS') || errorMessage.includes('already exists')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (errorMessage.includes('User not found') || errorMessage.includes('not found')) {
        setError('No account found with this email. Please sign up first.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        // Provide a more user-friendly error message
        setError(errorMessage || `${isSignUp ? 'Sign up' : 'Sign in'} failed. Please try again.`);
      }
      
      setLoading(false);
    }
  };

  const handleSocialAuth = async (provider: 'google' | 'github') => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log(`Attempting ${provider} authentication...`);
      let result;
      
      if (provider === 'google') {
        // ⚠️ NOTE: This is Blink SDK authentication for app login
        // This is SEPARATE from Gmail OAuth for email access (see EmailConnectionCard)
        result = await blink.auth.signInWithGoogle();
      } else if (provider === 'github') {
        result = await blink.auth.signInWithGitHub();
      }
      
      console.log(`${provider} authentication result:`, result);
      
      // Initialize defaults for new users after successful authentication (non-blocking)
      if (result?.id) {
        console.log('Initializing user defaults for social auth...');
        initializeNewUserDefaults(result.id, result.email || '').catch(err => {
          console.warn('Failed to initialize defaults (non-critical):', err);
        });
      }
      
      // Navigation will be handled by onAuthStateChanged
      console.log(`${provider} authentication successful`);
      // Keep loading state until navigation happens
    } catch (err: any) {
      // Handle popup cancellation gracefully (user closed the window or pressed cancel)
      const errorMessage = err?.message || err?.error?.message || String(err);
      const errorCode = err?.code || err?.error?.code;
      
      if (errorCode === 'POPUP_CANCELED' || errorMessage.includes('canceled') || errorMessage.includes('cancelled')) {
        console.log(`${provider} authentication was canceled by user`);
        // Simply reset loading state, don't show error for user-initiated cancellation
        setLoading(false);
        return;
      }
      
      // Handle popup blocked by browser
      if (errorCode === 'POPUP_BLOCKED' || errorMessage.includes('blocked') || errorMessage.includes('popup')) {
        console.error(`${provider} authentication popup blocked:`, err);
        setError(`Popup blocked! Please allow popups for this site in your browser settings and try again.`);
        setLoading(false);
        return;
      }
      
      // Handle network errors
      if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
        console.error(`${provider} authentication network error:`, err);
        setError('Network error. Please check your connection and try again.');
        setLoading(false);
        return;
      }
      
      // Log other errors and show user feedback
      console.error(`${provider} authentication error:`, err);
      
      // Use userMessage if available for better UX, otherwise provide a helpful error
      const friendlyError = err.userMessage || errorMessage || `${provider} authentication failed. Please try again.`;
      setError(friendlyError);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background gradient-mesh">
      <div className="absolute inset-0 gradient-dark opacity-50" />
      
      <Card className="w-full max-w-md z-10 glass animate-slide-up">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Naible-Thread
          </CardTitle>
          <CardDescription className="text-center">
            {isSignUp ? 'Create an account to get started' : 'Sign in to manage your emails with AI'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="animate-fade-in">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="animate-fade-in border-primary bg-primary/10">
              <AlertDescription className="text-primary">{success}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleEmailAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="demo@lemon-ai.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full gradient-primary hover:opacity-90" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isSignUp ? 'Creating account...' : 'Signing in...'}
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  {isSignUp ? 'Create Account' : 'Sign in with Email'}
                </>
              )}
            </Button>
          </form>
          
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
                setSuccess('');
              }}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              disabled={loading}
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {availableProviders.includes('google') && (
              <Button
                variant="outline"
                onClick={() => handleSocialAuth('google')}
                disabled={loading}
                className="hover:bg-primary/10 hover:border-primary transition-colors"
              >
                <Chrome className="mr-2 h-4 w-4" />
                Google
              </Button>
            )}
            
            {availableProviders.includes('github') && (
              <Button
                variant="outline"
                onClick={() => handleSocialAuth('github')}
                disabled={loading}
                className="hover:bg-primary/10 hover:border-primary transition-colors"
              >
                <Github className="mr-2 h-4 w-4" />
                GitHub
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}